import React, { createContext, PropsWithChildren, useCallback, useContext, useRef } from "react"

import { MediaContextInfo } from "types/mediaContext"

/**
 * Internal state for tracking media context.
 * Includes all fields needed to build MediaContextInfo.
 */
interface MediaContextState {
  resourceId: string
  currentOffset: string
  totalLength?: string
  taskId?: string
  title?: string
  // PDF-specific fields
  viewportTopLeft?: { x: number; y: number }
  viewportBottomRight?: { x: number; y: number }
}

/**
 * Saved position for media that can be resumed later.
 */
export interface SavedPosition {
  offset: string // Time offset "MM:SS" or "HH:MM:SS"
  viewport?: {
    // For page-based media
    topLeft: { x: number; y: number }
    bottomRight: { x: number; y: number }
  }
}

/**
 * Parameters for setting media context.
 */
export interface SetMediaContextParams {
  resourceId: string
  totalLength?: string
  taskId?: string
  title?: string
  initialOffset?: string
}

interface MediaContextType {
  /**
   * Set the active media context. Called by media players on mount.
   */
  setMediaContext: (params: SetMediaContextParams) => void

  /**
   * Update the current playback offset. Called periodically by media players.
   */
  updateOffset: (offset: string) => void

  /**
   * Update the total length. Called by media players after loading media duration.
   */
  updateTotalLength: (totalLength: string) => void

  /**
   * Update PDF viewport information.
   */
  updateViewport: (topLeft: { x: number; y: number }, bottomRight: { x: number; y: number }) => void

  /**
   * Clear the media context. Called by media players on unmount.
   */
  clearMediaContext: () => void

  /**
   * Save the current playback position into the resume map without clearing the
   * media context. Called by `ButtonCluster` when the user starts a Talk or Type
   * interaction so the next time they reopen the same media we can resume from
   * where they left off, even though the player may auto-pause/unmount in the
   * background.
   */
  saveCurrentPosition: () => void

  /**
   * Get the current media context info for sending with messages.
   * Returns undefined if no media is currently active.
   */
  getMediaContextInfo: () => MediaContextInfo | undefined

  /**
   * Get the last saved position for a media resource.
   * Used to resume playback where the user left off.
   */
  getLastPosition: (resourceId: string) => SavedPosition | undefined

  /**
   * Signal that the next PlayVideo / OpenResource backend event should be
   * suppressed because the user already launched the player directly.
   * The flag is automatically cleared after a single consumption.
   */
  suppressNextMediaOpen: () => void

  /**
   * Read and clear the suppress-once flag. Returns true if the caller should
   * skip opening the media player from the incoming backend event.
   */
  consumeSuppressNextMediaOpen: () => boolean
}

const MediaContext = createContext<MediaContextType | null>(null)

/**
 * MediaContextProvider tracks the currently active media (video, audio, PDF)
 * and its playback state. When users send messages while viewing media,
 * the media_context_info can be retrieved to include in the websocket message.
 */
export const MediaContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  // Using ref to avoid re-renders when offset updates frequently
  const mediaStateRef = useRef<MediaContextState | null>(null)
  // Store last positions for media that can be resumed later (in-memory only)
  const lastPositionsRef = useRef<Map<string, SavedPosition>>(new Map())
  // Suppress the next PlayVideo/OpenResource backend event (consume-once flag)
  const suppressNextMediaOpenRef = useRef(false)

  const setMediaContext = useCallback((params: SetMediaContextParams) => {
    const existing = mediaStateRef.current
    const isSameResource = existing?.resourceId === params.resourceId

    mediaStateRef.current = {
      resourceId: params.resourceId,
      currentOffset: params.initialOffset ?? "00:00",
      // Only preserve totalLength if same resource and new value is undefined
      totalLength: isSameResource
        ? (params.totalLength ?? existing?.totalLength)
        : params.totalLength,
      taskId: params.taskId ?? (isSameResource ? existing?.taskId : undefined),
      title: params.title ?? (isSameResource ? existing?.title : undefined),
    }
  }, [])

  const updateOffset = useCallback((offset: string) => {
    if (mediaStateRef.current) {
      mediaStateRef.current.currentOffset = offset
    }
  }, [])

  const updateTotalLength = useCallback((totalLength: string) => {
    if (mediaStateRef.current) {
      mediaStateRef.current.totalLength = totalLength
    }
  }, [])

  const updateViewport = useCallback(
    (topLeft: { x: number; y: number }, bottomRight: { x: number; y: number }) => {
      if (mediaStateRef.current) {
        mediaStateRef.current.viewportTopLeft = topLeft
        mediaStateRef.current.viewportBottomRight = bottomRight
      }
    },
    [],
  )

  const saveCurrentPosition = useCallback(() => {
    // Save current position without clearing the context
    const state = mediaStateRef.current
    if (state && state.resourceId) {
      const savedPosition: SavedPosition = {
        offset: state.currentOffset,
      }
      // Save viewport if available (for page-based media)
      if (state.viewportTopLeft && state.viewportBottomRight) {
        savedPosition.viewport = {
          topLeft: state.viewportTopLeft,
          bottomRight: state.viewportBottomRight,
        }
      }
      lastPositionsRef.current.set(state.resourceId, savedPosition)
    }
  }, [])

  const clearMediaContext = useCallback(() => {
    // Save current position before clearing
    saveCurrentPosition()
    mediaStateRef.current = null
  }, [saveCurrentPosition])

  const getMediaContextInfo = useCallback((): MediaContextInfo | undefined => {
    const state = mediaStateRef.current
    if (!state) {
      return undefined
    }

    const info: MediaContextInfo = {
      resource_id: state.resourceId,
      offset: state.currentOffset,
    }

    if (state.totalLength) {
      info.total_length = state.totalLength
    }
    if (state.taskId) {
      info.cm_task_id = state.taskId
    }
    if (state.title) {
      info.cm_title = state.title
    }
    if (state.viewportTopLeft) {
      info.viewport_top_left = state.viewportTopLeft
    }
    if (state.viewportBottomRight) {
      info.viewport_bottom_right = state.viewportBottomRight
    }

    return info
  }, [])

  const getLastPosition = useCallback((resourceId: string): SavedPosition | undefined => {
    return lastPositionsRef.current.get(resourceId)
  }, [])

  const suppressNextMediaOpen = useCallback(() => {
    suppressNextMediaOpenRef.current = true
  }, [])

  const consumeSuppressNextMediaOpen = useCallback((): boolean => {
    if (suppressNextMediaOpenRef.current) {
      suppressNextMediaOpenRef.current = false
      return true
    }
    return false
  }, [])

  const value: MediaContextType = {
    setMediaContext,
    updateOffset,
    updateTotalLength,
    updateViewport,
    clearMediaContext,
    saveCurrentPosition,
    getMediaContextInfo,
    getLastPosition,
    suppressNextMediaOpen,
    consumeSuppressNextMediaOpen,
  }

  return <MediaContext.Provider value={value}>{children}</MediaContext.Provider>
}

/**
 * Hook to access media context functionality.
 */
export const useMediaContext = (): MediaContextType => {
  const context = useContext(MediaContext)
  if (!context) {
    throw new Error("useMediaContext must be used within a MediaContextProvider")
  }
  return context
}
